import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useSessionStorage } from "usehooks-ts";

import MobileNavbar from "$/lib/components/molecules/Navbar/MobileNavbar";
import Navbar from "$/lib/components/molecules/Navbar/Navbar";
import { useAuth } from "$/lib/providers/AuthProvider";
import { SubscriptionStatusEnum } from "$/lib/types/models/Subscription.types";
import { ROUTES } from "$/lib/utils/constants/routes.constants";
import BankSyncTracker from "$/routes/_user-layout/-features/components/BankSyncTracker";
import SubscriptionRequired from "$/routes/_user-layout/-features/components/SubscriptionRequired";

export const Route = createFileRoute("/_user-layout")({
  component: UserLayout,
});

function UserLayout() {
  const { user, isFetching } = useAuth();
  const location = useLocation();
  const [, setShowEstimateData] = useSessionStorage(
    "financial-report-show-estimate-data",
    false,
  );

  const subscriptionStatus = user?.subscription?.status ?? null;

  const isPersonalInfoPage = location.pathname.startsWith(ROUTES.USER_INFO);
  const isSubscriptionPage = location.pathname.startsWith(ROUTES.SUBSCRIPTION);
  const isHelpPage = location.pathname.startsWith(ROUTES.HELP_HOME);

  const activeStatuses = new Set<SubscriptionStatusEnum>([
    SubscriptionStatusEnum.ACTIVE,
    SubscriptionStatusEnum.TRIALING,
    SubscriptionStatusEnum.PAUSED,
    SubscriptionStatusEnum.CANCELLING,
  ]);

  const canEvaluateSubscription = Boolean(user) && !isFetching;
  const hasValidSubscription =
    canEvaluateSubscription &&
    subscriptionStatus !== null &&
    activeStatuses.has(subscriptionStatus);

  // Check if user is in free trial period
  const isInFreeTrial = user?.isInFreeTrial ?? false;

  // Check if user has started their trial (connected Bridge account)
  const hasStartedTrial = user?.freeTrialStartedAt != null;

  // User should only be blocked if:
  // 1. They have connected Bridge (trial started)
  // 2. Trial has expired (not in free trial anymore)
  // 3. They don't have a valid subscription
  const shouldGateContent =
    canEvaluateSubscription &&
    !isPersonalInfoPage &&
    !isSubscriptionPage &&
    !isHelpPage &&
    hasStartedTrial &&
    !isInFreeTrial &&
    !hasValidSubscription;

  useEffect(() => {
    if (!user && !isFetching) setShowEstimateData(false);
  }, [user, isFetching]);

  if (user && !user.isVerified) {
    return (
      <Navigate
        to={ROUTES.VERIFY_EMAIL}
        search={{ redirect: location.pathname }}
        // TODO: infer the typing from `Navigate`
        // @ts-expect-error state typing
        state={{ fromAuth: true }}
      />
    );
  }

  if (!user && !isFetching) return <Navigate to={ROUTES.LOGIN} />;

  return (
    <div className="mx-auto w-[100%] mt-1 screen850:overflow-x-auto">
      <MobileNavbar />
      <div className="mx-auto w-full pb-24 screen850:min-w-[1100px] max-w-[1200px] screen850:pt-3 screen850:pb-3">
        <div className="hidden screen850:block">
          <Navbar />
        </div>

        {/* La bannière de période d'essai a été retirée d'ici */}
        {/* Elle est maintenant uniquement affichée sur la page d'accueil */}

        {/* Subscription Required Popup */}
        {shouldGateContent && (
          <SubscriptionRequired status={subscriptionStatus} />
        )}

        {/* Tracks a post-connection bank sync across route changes */}
        <BankSyncTracker />

        <Outlet />
      </div>
    </div>
  );
}
